import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function useTabs<T extends string>(
    tabs: Array<{ key: T;[key: string]: any }>,
    initialTab: T,
    urlPattern?: (tab: T) => string
) {
    const navigate = useNavigate()
    const getTabIndex = useCallback((tabKey: T): number => {
        const index = tabs?.findIndex(tab => tab.key === tabKey)
        return index >= 0 ? index : 0
    }, [tabs])

    const getTabKey = useCallback((index: number): T => {
        return tabs[index]?.key ?? tabs[0]?.key
    }, [tabs])

    const [activeIndex, setActiveIndex] = useState(() => getTabIndex(initialTab))

    useEffect(() => {
        const newIndex = getTabIndex(initialTab)
        setActiveIndex(newIndex)
    }, [initialTab, getTabIndex])

    const setActiveTab = useCallback((tab: T) => {
        const index = getTabIndex(tab)
        setActiveIndex(index)
        if (urlPattern) {
            navigate(urlPattern(tab), { replace: true })
        }
    }, [getTabIndex, navigate, urlPattern])

    const setActiveIndexHandler = useCallback((index: number) => {
        setActiveIndex(index)
        const tabKey = getTabKey(index)
        if (urlPattern) {
            navigate(urlPattern(tabKey), { replace: true })
        }
    }, [getTabKey, navigate, urlPattern])

    return {
        activeIndex,
        activeTab: getTabKey(activeIndex),
        setActiveTab,
        setActiveIndex: setActiveIndexHandler,
        getTabIndex,
        getTabKey
    }
}